import { getIsValidId } from '@/store/asyncActions';
import { useRouter } from 'next/router';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useContextState } from '../../context/auth';
import Loader from '../Common/Loader';

type Props = {
  children: JSX.Element;
  accessType: 'authenticated' | 'anonymous' | null;
};

function extractRouteAndParams(url: string) {
  const urlObj = new URL(url, 'http://example.com'); // The base URL is needed for the URL constructor
  let subRoute = urlObj.pathname;
  const queryParams = Object.fromEntries(new URLSearchParams(urlObj.search));

  if (subRoute.endsWith('/')) {
    subRoute = subRoute.slice(0, -1);
  }

  return {
    subRoute,
    queryParams,
  };
}

export function AuthGuard({ accessType, children }: Props) {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isValidId, setIsValidId] = useState(true);
  const [canRender, setCanRender] = useState(false);
  const [isChangingRoute, setIsChangingRoute] = useState(false);
  const [onlyTabChanged, setOnlyTabChanged] = useState(false);
  const validatedIdsRef = useRef<Set<string>>(new Set());

  const contextState = useContextState();
  const status = contextState?.loginStatus;
  const router = useRouter();
  const { id, tabId } = router.query;

  const previousStateRef = useRef({
    subRoute: router.pathname,
    tabId: (tabId as string) || '',
    id: (id as string) || '',
  });

  const handleRouteChangeStart = useCallback((newUrl: string, ...v: any) => {
    let { subRoute, queryParams } = extractRouteAndParams(newUrl);
    const prevState = previousStateRef.current;

    // Check if this is only a tab change within the same route
    if (
      subRoute === prevState.subRoute &&
      queryParams.id === prevState.id &&
      queryParams.tabId !== prevState.tabId
    ) {
      setOnlyTabChanged(true);
    } else {
      // This is a route change (different path or different id)
      setIsChangingRoute(true);
      setOnlyTabChanged(false);

      // If the actual page (subRoute) changed, force a remount of the
      // children by flipping `canRender` to false. Without this, AuthGuard
      // would keep the previous page's React tree mounted while Next.js
      // tries to swap the `Component` prop in `_app.tsx` — and Next.js
      // occasionally drops that React-side update under load, leaving the
      // user stranded on the old page with a new URL. Unmount + remount
      // gives React a clean break and forces it to pick up the new
      // Component. The `validatedIdsRef` cache still skips the network
      // call below, so this is essentially free for already-seen IDs.
      const subRouteChanged = subRoute !== prevState.subRoute;
      const idChangedAndUnvalidated =
        queryParams.id &&
        queryParams.id !== prevState.id &&
        !validatedIdsRef.current.has(queryParams.id);

      if (subRouteChanged || idChangedAndUnvalidated) {
        setCanRender(false);
      }
    }

    previousStateRef.current = {
      subRoute,
      tabId: queryParams.tabId,
      id: queryParams.id,
    };
  }, []);

  const handleRouteChangeComplete = useCallback(() => {
    setIsChangingRoute(false);
    setOnlyTabChanged(false);
  }, []);

  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeComplete);
    };
  }, [router.events, handleRouteChangeStart, handleRouteChangeComplete]);

  const isValidIdCheck = useCallback(
    async (id: string) => {
      // Skip if already validated
      if (validatedIdsRef.current.has(id)) {
        setCanRender(true);
        return true;
      }

      try {
        const res = await getIsValidId(id);
        if (res.status === 'success') {
          validatedIdsRef.current.add(id);
          setCanRender(true);
          return true;
        } else {
          setIsValidId(false);
          router.push('/page_not_found');
          return false;
        }
      } catch (err) {
        console.error('Error validating ID:', err);
        setIsValidId(false);
        router.push('/page_not_found');
        return false;
      }
    },
    [router],
  );

  const checkAuthentication = useCallback(async () => {
    // Wait for auth status to be determined
    if (status === 'pending') {
      return;
    }

    // Reset isValidId for new authentication checks
    setIsValidId(true);

    if (accessType === 'authenticated') {
      if (status === 'loggedOut') {
        router.replace('/sign_in');
        return;
      }
      if (id) {
        // Only show loader if ID hasn't been validated before
        if (!validatedIdsRef.current.has(id as string)) {
          setCanRender(false);
        }
        await isValidIdCheck(id as string);
      } else {
        setCanRender(true);
      }
    } else if (accessType === 'anonymous' && status === 'loggedIn') {
      router.replace('/home');
      return;
    } else {
      setCanRender(true);
    }

    setIsInitialLoad(false);
  }, [accessType, status, id, isValidIdCheck, router]);

  useEffect(() => {
    if (router.isReady && !isChangingRoute && !onlyTabChanged) {
      checkAuthentication();
    }
  }, [router.isReady, isChangingRoute, onlyTabChanged, checkAuthentication]);

  // Reset onlyTabChanged when login status changes (like during logout)
  useEffect(() => {
    setOnlyTabChanged(false);
  }, [status]);

  if (!canRender || !isValidId) {
    return <Loader />;
  }

  return <>{children}</>;
}
